/**
 * Pet Finder Detail Component
 * - Displays lost/found pet report details
 * - Shows pet information, location, and contact details
 * - Includes reward information and posting date
 * - Features image gallery with modal viewer
 */

import React, { useState } from "react";
import { FaRegCalendarAlt, FaRegUser, FaPaw, FaVenusMars, FaPalette, FaInfoCircle, FaRegEnvelope } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { HiCurrencyDollar } from "react-icons/hi";
import { MdPhoneInTalk } from "react-icons/md";
import AvatarBlock from "./Shared/AvatarBlock";
import InfoRow from "./Shared/InfoRow";
import StatusBadge from "./Shared/StatusBadge";
import SectionHeader from "./Shared/SectionHeader";
import ImageModal from "../common/ImageModal";
import { formatDateTime } from "./Shared/helpers";

const PetFinderDetail = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const pet = data.breed && data.pet_name ? `${data.breed} - ${data.pet_name}` : "NA";
  const location = data.last_known_location || "NA";
  const name = data.user_details?.name || "NA";
  const mobile = data.user_details?.mobile || "NA";
  const email = data.user_details?.email || "NA";
  const submitted = formatDateTime(data.created_at);
  const description = data.additional_info || "NA";
  const sex = data.sex || "NA";
  const age = data.age || "NA";
  const physical_marks = data.physical_marks || "NA";
  const color = data.color || "NA";
  const image = data.image_urls?.[0] || "https://placehold.co/80x80";
  const reward = data.reward || "NA";
  const status = data.active ? "Active" : "Inactive";

  // All info rows together
  const infoItems = [
    { icon: FaRegUser, label: `Posted by: ${name}` },
    { icon: FaPaw, label: `Pet: ${pet}` },
    { icon: FaLocationDot, label: `Location: ${location}` },
    { icon: FaVenusMars, label: `Sex: ${sex}` },
    { icon: FaPalette, label: `Color: ${color}` },
    { icon: FaRegCalendarAlt, label: `Posted: ${submitted}` },
    { icon: HiCurrencyDollar, label: `Reward: ${reward}` },
    { icon: FaRegCalendarAlt, label: `Age: ${age}` },
    { icon: FaInfoCircle, label: `Physical Marks: ${physical_marks}` },
  ];

  // Split into two columns dynamically (left/right)
  const mid = Math.ceil(infoItems.length / 2);
  const leftInfo = infoItems.slice(0, mid);
  const rightInfo = infoItems.slice(mid);

  return (
    <div className="flex flex-col gap-8 mt-6 lg:mt-10">
      {/* Header with Avatar + Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-items-start gap-4 lg:gap-40">
        <AvatarBlock image={image} name={pet} onImageClick={setSelectedImage} />
        <StatusBadge status={status} />
      </div>

      {/* Info Section in Two Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-8 text-gray-800 text-sm sm:text-base mt-2 pl-4  lg:ml-20">
        {/* Left Column */}
        <div className="flex flex-col gap-3">
          {leftInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-3">
          {rightInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>
      </div>

      {/* Images Section */}
      <div>
        <SectionHeader className="mt-2 lg:mt-6">Images</SectionHeader>
        <div className="mt-4 flex flex-wrap gap-4">
          {data.image_urls && data.image_urls.length > 0 ? (
            data.image_urls.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Pet image ${index + 1}`}
                className="w-20 h-20 object-cover rounded-lg cursor-pointer"
                onClick={() => setSelectedImage(image)}
              />
            ))
          ) : (
            <img
              src="https://placehold.co/80x80"
              alt="No image available"
              className="w-20 h-20 object-cover rounded-lg"
            />
          )}
        </div>
      </div>

      {/* Description Section */}
      {description && description !== "NA" && (
        <div>
          <SectionHeader className="mt-2 lg:mt-6">Description</SectionHeader>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-700 whitespace-pre-wrap">{description}</p>
          </div>
        </div>
      )}

      {/* Contact Information Section */}
      <div>
        <SectionHeader className="mt-2 lg:mt-6">Contact Information</SectionHeader>
        <div className="mt-4 flex flex-col gap-2 px-4">
          <InfoRow icon={MdPhoneInTalk}>{mobile}</InfoRow>
          <InfoRow icon={FaRegEnvelope}>{email}</InfoRow>
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText={`${pet} image`}
      />
    </div>
  );
};

export default PetFinderDetail;
